import ArrowDownIcon from "@/assets/icons/arrow-down.svg";
import { ArtifactMarkdown } from "@/component/ArtifactMarkdown";
import { AudioWavePlayer } from "@/component/AudioWavePlayer";
import { ArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { Tabs } from "antd";
import classNames from "classnames";
import isEqual from "fast-deep-equal";
import React, { useState } from "react";
import { Section } from "./Section";

type MusicAnalysisSectionProps = ArtifactsViewerData["musicAnalysis"];

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * 将毫秒数格式化为 mm:ss 格式
 */
function formatTimeFromMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`;
}

export const MusicAnalysisSection = React.memo(
  function MusicAnalysisSection(props: MusicAnalysisSectionProps) {
    const { id, data, visible, title } = props;
    const [expandedLyrics, setExpandedLyrics] = useState(false);

    if (!visible) {
      return null;
    }

    const { analysisResult, lyrics } = data;
    const hasLyrics = lyrics && lyrics.length > 0;
    const globalFeatures = analysisResult?.analysis_result?.global_features;

    const tabItems = [
      {
        key: "details",
        label: "Audio Details",
        children: (
          <>
            {/* Metrics Row */}
            {globalFeatures && (
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div className="p-3 pl-0">
                  <div className="text-xs text-text-secondary mb-0.5">BPM</div>
                  <div className="text-xl font-semibold text-text-primary">
                    {typeof globalFeatures.tempo_bpm === "number"
                      ? globalFeatures.tempo_bpm.toFixed(0)
                      : "-"}
                  </div>
                </div>
                <div className="p-3 pl-0">
                  <div className="text-xs text-text-secondary mb-0.5">Key</div>
                  <div className="text-xl font-semibold text-text-primary">
                    {[globalFeatures.key, globalFeatures.scale]
                      .filter(Boolean)
                      .join(" ") || "-"}
                  </div>
                </div>
                <div className="p-3 pl-0">
                  <div className="text-xs text-text-secondary mb-0.5">
                    Duration
                  </div>
                  <div className="text-xl font-semibold text-text-primary">
                    {typeof globalFeatures.duration === "number"
                      ? formatDuration(globalFeatures.duration)
                      : "-"}
                  </div>
                </div>
              </div>
            )}

            {/* Summary Block (no collapse) */}
            {analysisResult?.summary && (
              <ArtifactMarkdown>
                {analysisResult.summary
                  .replaceAll("[", "\\[")
                  .replaceAll("]", "\\]")}
              </ArtifactMarkdown>
            )}
          </>
        ),
      },
      {
        key: "lyrics",
        label: "Lyrics",
        children: hasLyrics ? (
          <div className="overflow-hidden">
            <div
              className={classNames(
                "transition-all duration-300 overflow-hidden",
                !expandedLyrics && [
                  "max-h-48",
                  "[mask-image:linear-gradient(to_bottom,black,black_calc(100%-150px),transparent)]",
                  "[-webkit-mask-image:linear-gradient(to_bottom,black,black_calc(100%-150px),transparent)]",
                ]
              )}
            >
              {lyrics.map((line, index) => (
                <div key={index} className="flex gap-4 items-start text-sm p-2">
                  <span className="text-text-tertiary font-mono shrink-0 min-w-[80px]">
                    {`${formatTimeFromMs(line.start)} - ${formatTimeFromMs(
                      line.end
                    )}`}
                  </span>
                  <span className="text-text-primary">{line.text}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-center pt-1 pb-2">
              <button
                type="button"
                onClick={() => setExpandedLyrics(!expandedLyrics)}
                className="my-2 px-4 text-text-primary rounded-full h-9 text-xs flex items-center justify-center gap-2 font-medium transition-all cursor-pointer"
              >
                {expandedLyrics ? "Collapse" : "Expand"}
                <ArrowDownIcon
                  className={`w-4 h-4 transition-transform duration-300 ${
                    expandedLyrics ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-text-tertiary text-center py-8">
            No lyrics available
          </div>
        ),
      },
    ];

    return (
      <Section id={id} title={title}>
        {analysisResult?.music_file_path && (
          <div className="mb-4">
            <AudioWavePlayer
              audioUrl={analysisResult.music_file_path}
              title="Music Track"
              analysisData={{
                beats: analysisResult.analysis_result?.beats,
                beat_positions: analysisResult.analysis_result?.beat_positions,
                downbeats: analysisResult.analysis_result?.downbeats,
                phrases: analysisResult.analysis_result?.phrases,
              }}
            />
          </div>
        )}
        <Tabs defaultActiveKey="details" items={tabItems} />
      </Section>
    );
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  }
);
